// Live's "Saved" on a generated portrait, on every visit (issue 407): the job read names the
// team's actor each `image.actor` portrait was saved as (`GenerationOutput.saved_actor_id`, live's
// `savedImageId`), until that actor is deleted. A save or a delete shows in My Generations at once.
import type { QueryClient } from "@tanstack/react-query";

import type { AiActor, GenerationOutput, Job } from "../../../api/generated/model";
import { patchListedJobs } from "../../../platform/jobs/job-list";

/** A portrait kept as one of the team's actors. */
export const isSaved = (output: GenerationOutput) =>
  output.saved_actor_id !== undefined && output.saved_actor_id !== null;

/** Each saved actor, in the order its indexes were asked (the save answers in that order). */
export function markSaved(
  queryClient: QueryClient,
  teamId: string,
  jobId: string,
  indexes: readonly number[],
  actors: readonly AiActor[],
) {
  const saved = new Map<number, string>();
  for (const [at, index] of indexes.entries()) {
    const actor = actors[at];
    if (actor !== undefined) saved.set(index, actor.id);
  }
  patchListedJobs(queryClient, teamId, (job) =>
    job.id !== jobId
      ? job
      : withOutputs(job, (output) => saved.get(output.index) ?? output.saved_actor_id),
  );
}

/** A deleted actor's portrait is not saved any more, as the next read says. */
export function markUnsaved(queryClient: QueryClient, teamId: string, actorId: string) {
  patchListedJobs(queryClient, teamId, (job) =>
    job.outputs.every((output) => output.saved_actor_id !== actorId)
      ? job
      : withOutputs(job, (output) =>
          output.saved_actor_id === actorId ? null : output.saved_actor_id,
        ),
  );
}

function withOutputs(
  job: Job,
  savedAs: (output: GenerationOutput) => string | null | undefined,
): Job {
  const outputs = job.outputs.map((output) => ({ ...output, saved_actor_id: savedAs(output) ?? null }));
  return { ...job, outputs };
}
